import React, {useRef, useState} from "react";
import {EventResult} from "event-series-shared/src/types/EventResult";
import {createEvent} from "../Api";

const PencilIcon = () => (
    <svg width="12" height="12" viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round">
        <path d="M8.5 1.5L10.5 3.5L3.5 10.5H1.5V8.5L8.5 1.5Z"/>
        <line x1="7" y1="3" x2="9" y2="5"/>
    </svg>
);

interface Props {
    events: EventResult[];
    seriesId: number;
    mode: '12' | '24';
    onModeChange: (mode: '12' | '24') => void;
    onEventAdded: () => void;
    onEditRequest: (ev: EventResult) => void;
    onClose: () => void;
}

const pad = (n: number) => String(n).padStart(2, '0');
const toDateStr = (d: Date) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const toTimeStr = (d: Date) => `${pad(d.getHours())}:${pad(d.getMinutes())}`;

const defaultTimes = () => {
    const end = new Date();
    const start = new Date(end.getTime() - 10 * 60 * 1000);
    return {
        startDate: toDateStr(start), startTime: toTimeStr(start),
        endDate: toDateStr(end),     endTime: toTimeStr(end),
    };
};

function DetailsModal({events, seriesId, mode, onModeChange, onEventAdded, onEditRequest, onClose}: Props) {

    const defaults = defaultTimes();
    const [startDate, setStartDate] = useState(defaults.startDate);
    const [startTime, setStartTime] = useState(defaults.startTime);
    const [endDate, setEndDate] = useState(defaults.endDate);
    const [endTime, setEndTime] = useState(defaults.endTime);
    const [formError, setFormError] = useState('');
    const [saving, setSaving] = useState(false);
    const [tooltipEvents, setTooltipEvents] = useState<EventResult[]>([]);
    const [tooltipPos, setTooltipPos] = useState({ x: 0, y: 0 });
    const tooltipHideRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const timelineRef = useRef<HTMLDivElement>(null);

    const scheduleTooltipHide = () => {
        tooltipHideRef.current = setTimeout(() => setTooltipEvents([]), 150);
    };
    const cancelTooltipHide = () => {
        if (tooltipHideRef.current) {
            clearTimeout(tooltipHideRef.current);
            tooltipHideRef.current = null;
        }
    };

    const makeLabel = (hour: number, minute: number) =>
        hour + ':' + (minute < 10 ? '0' + minute : '' + minute);

    // ── helpers ──────────────────────────────────────────────────────────────

    const parseStart = (ev: EventResult) => new Date(String(ev.start));
    const parseEnd   = (ev: EventResult) => new Date(String(ev.end));

    const sorted = [...events].sort((a, b) => parseStart(a).getTime() - parseStart(b).getTime());

    // ── stats ─────────────────────────────────────────────────────────────────

    const renderStats = () => {
        if (events.length === 0) return <p className="no-data">No events yet.</p>;

        const firstTime  = parseStart(sorted[0]).getTime();
        const weeksSpan  = Math.max((Date.now() - firstTime) / (7 * 24 * 60 * 60 * 1000), 1 / 7);
        const totalMins  = events.reduce((s, ev) => s + (parseEnd(ev).getTime() - parseStart(ev).getTime()) / 60000, 0);

        const eventsPerWeek  = (events.length / weeksSpan).toFixed(1);
        const minutesPerWeek = Math.round(totalMins / weeksSpan);

        const latest = sorted[sorted.length - 1];
        const latestStart = parseStart(latest);
        const latestDateStr = latestStart.toLocaleDateString(undefined, {month: 'short', day: 'numeric', year: 'numeric'});
        const latestStartH = mode === '24' ? latest.startHour24 : latest.startHour;
        const latestEndH   = mode === '24' ? latest.endHour24   : latest.endHour;
        const latestTimeStr = makeLabel(latestStartH, latest.startMinute) + ' – ' + makeLabel(latestEndH, latest.endMinute);

        return (
            <>
            <div className="stats-row">
                <div className="stat-item">
                    <span className="stat-value">{eventsPerWeek}</span>
                    <span className="stat-label">events / week</span>
                </div>
                <div className="stat-item">
                    <span className="stat-value">{minutesPerWeek}</span>
                    <span className="stat-label">minutes / week</span>
                </div>
                <div className="stat-item">
                    <span className="stat-value">{events.length}</span>
                    <span className="stat-label">total events</span>
                </div>
            </div>
            <div className="stat-latest">
                <span className="stat-label">Most recent: </span>
                <span className="stat-value-inline">{latestDateStr} {latestTimeStr}</span>
            </div>
            </>
        );
    };

    // ── timeline ──────────────────────────────────────────────────────────────

    const renderTimeline = () => {
        if (events.length === 0) return null;

        const padLeft   = 36;
        const padRight  = 12;
        const padTop    = 8;
        const plotH     = 110;
        const padBottom = 25;
        const lineWidth = 576;
        const totalW    = padLeft + lineWidth + padRight;
        const totalH    = padTop + plotH + padBottom;
        const baselineY = padTop + plotH;

        const firstTime = parseStart(sorted[0]).getTime();
        const nowTime   = Date.now();
        const span      = Math.max(nowTime - firstTime, 1);

        const toX = (t: number) => padLeft + ((t - firstTime) / span) * lineWidth;
        const toY = (pct: number) => padTop + (1 - pct / 100) * plotH;

        const isMinuteCovered = (hour: number, minute: number, startHour: number, startMinute: number, endHour: number, endMinute: number): boolean => {
            const wrapsAround = startHour > endHour || (startHour === endHour && startMinute > endMinute);
            const startBefore = startHour < hour || (startHour === hour && startMinute <= minute);
            const endAfter    = endHour > hour   || (endHour === hour   && endMinute >= minute);
            return wrapsAround ? (startBefore || endAfter) : (startBefore && endAfter);
        };

        const hours = mode === '12'
            ? Array.from({length: 12}, (_, i) => i + 1)
            : Array.from({length: 24}, (_, i) => i);
        const totalMinutes = hours.length * 60;
        const coveredSlots = new Set<number>();
        const completionPcts = sorted.map(ev => {
            const startH = mode === '24' ? ev.startHour24 : ev.startHour;
            const endH   = mode === '24' ? ev.endHour24   : ev.endHour;
            for (const hour of hours) {
                for (let minute = 0; minute < 60; minute++) {
                    if (isMinuteCovered(hour, minute, startH, ev.startMinute, endH, ev.endMinute)) {
                        coveredSlots.add(hour * 60 + minute);
                    }
                }
            }
            return coveredSlots.size / totalMinutes * 100;
        });

        const fmtEnd = (d: Date) => d.toLocaleDateString(undefined, {month: 'short', day: 'numeric', year: '2-digit'});
        const fmtMid = (d: Date) => d.toLocaleDateString(undefined, {month: 'short', year: 'numeric'});

        const midTicks = [1/3, 2/3].map(frac => ({
            x: padLeft + frac * lineWidth,
            label: fmtMid(new Date(firstTime + frac * span)),
        }));

        const THRESHOLD = 9;
        const handleDotEnter = (e: React.MouseEvent<SVGCircleElement>, ev: EventResult) => {
            cancelTooltipHide();
            const hovX = toX(parseStart(ev).getTime());
            const nearby = sorted.filter(other => Math.abs(toX(parseStart(other).getTime()) - hovX) <= THRESHOLD);
            const containerTop = timelineRef.current?.getBoundingClientRect().top ?? e.clientY;
            setTooltipPos({ x: e.clientX, y: containerTop });
            setTooltipEvents(nearby);
        };

        const yTicks = [0, 25, 50, 75, 100];

        return (
            <svg viewBox={`0 0 ${totalW} ${totalH}`} width="100%" style={{display: 'block'}}>
                {/* Y-axis */}
                <line x1={padLeft} y1={padTop} x2={padLeft} y2={baselineY} stroke="#444" strokeWidth="1"/>
                {yTicks.map(pct => (
                    <g key={pct}>
                        <line x1={padLeft - 4} y1={toY(pct)} x2={padLeft} y2={toY(pct)} stroke="#444" strokeWidth="1"/>
                        <text x={padLeft - 6} y={toY(pct) + 4} fill="#999" fontSize="10" textAnchor="end">{pct}%</text>
                    </g>
                ))}
                {/* X-axis */}
                <line x1={padLeft} y1={baselineY} x2={padLeft + lineWidth} y2={baselineY} stroke="#444" strokeWidth="1.5"/>
                <circle cx={padLeft} cy={baselineY} r="3" fill="#444"/>
                <circle cx={padLeft + lineWidth} cy={baselineY} r="3" fill="#444"/>
                {midTicks.map((t, i) => (
                    <g key={i}>
                        <line x1={t.x} y1={baselineY - 5} x2={t.x} y2={baselineY + 5} stroke="#444" strokeWidth="1"/>
                        <text x={t.x} y={totalH - 3} fill="#999" fontSize="11" textAnchor="middle">{t.label}</text>
                    </g>
                ))}
                <text x={padLeft} y={totalH - 3} fill="#999" fontSize="11" textAnchor="start">{fmtEnd(parseStart(sorted[0]))}</text>
                <text x={padLeft + lineWidth} y={totalH - 3} fill="#999" fontSize="11" textAnchor="end">Today</text>
                {/* Dots */}
                {sorted.map((ev, i) => (
                    <circle key={ev.eventId}
                            cx={toX(parseStart(ev).getTime())}
                            cy={toY(completionPcts[i])}
                            r="4.5"
                            fill="#3cc4f9" stroke="#1a1a1a" strokeWidth="1"
                            style={{cursor: 'pointer'}}
                            onMouseEnter={e => handleDotEnter(e, ev)}
                            onMouseLeave={scheduleTooltipHide}/>
                ))}
            </svg>
        );
    };

    // ── add event form ────────────────────────────────────────────────────────

    const addTenMinutes = (date: string, time: string) => {
        const d = new Date(`${date}T${time}`);
        d.setMinutes(d.getMinutes() + 10);
        return {
            date: `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`,
            time: `${pad(d.getHours())}:${pad(d.getMinutes())}`,
        };
    };

    const handleStartDateChange = (value: string) => {
        setStartDate(value);
        setEndDate(value);
    };

    const handleStartTimeChange = (value: string) => {
        setStartTime(value);
        if (startDate && value) {
            const end = addTenMinutes(startDate, value);
            setEndDate(end.date);
            setEndTime(end.time);
        }
    };

    const handleSave = async () => {
        if (!startDate || !startTime || !endDate || !endTime) {
            setFormError('Please fill in both times.');
            return;
        }
        if (new Date(`${endDate}T${endTime}`) <= new Date(`${startDate}T${startTime}`)) {
            setFormError('End time must be after start time.');
            return;
        }
        setSaving(true);
        try {
            await createEvent(`${startDate}T${startTime}`, `${endDate}T${endTime}`, seriesId);
            const d = defaultTimes(); setStartDate(d.startDate); setStartTime(d.startTime); setEndDate(d.endDate); setEndTime(d.endTime); setFormError('');
            setSaving(false);
            onEventAdded();
        } catch (e) {
            setFormError('Failed to save event.');
            setSaving(false);
        }
    };

    const endTimeMin = endDate === startDate ? startTime : '';

    // ── render ────────────────────────────────────────────────────────────────

    return (
        <>
        {tooltipEvents.length > 0 && (
            <div className="events-modal" style={{position: 'fixed', left: tooltipPos.x, top: tooltipPos.y, transform: 'translate(-50%, -100%)', zIndex: 30000}}
                 onMouseEnter={cancelTooltipHide}
                 onMouseLeave={scheduleTooltipHide}>
                {tooltipEvents.map(ev => {
                    const dateStr = String(ev.start).slice(0, String(ev.start).indexOf('T'));
                    const startH = mode === '24' ? ev.startHour24 : ev.startHour;
                    const endH   = mode === '24' ? ev.endHour24   : ev.endHour;
                    return (
                        <div key={ev.eventId} className="event-row">
                            <span>{dateStr} | {makeLabel(startH, ev.startMinute)} – {makeLabel(endH, ev.endMinute)}</span>
                            <button className="delete-btn" onClick={() => { setTooltipEvents([]); onEditRequest(ev); }}><PencilIcon /></button>
                        </div>
                    );
                })}
            </div>
        )}
        <div className="modal-overlay" onClick={onClose}>
            <div className="details-modal" onClick={e => e.stopPropagation()}>
                <div className="modal-title">Event Series</div>

                <div className="details-section-title">Stats</div>
                {renderStats()}

                <div className="details-section-title">Display</div>
                <div className="radio-group">
                    <label className="radio-label">
                        <input type="radio" name="mode" value="12" checked={mode === '12'} onChange={() => onModeChange('12')} />
                        12-hour
                    </label>
                    <label className="radio-label">
                        <input type="radio" name="mode" value="24" checked={mode === '24'} onChange={() => onModeChange('24')} />
                        24-hour
                    </label>
                </div>

                {events.length > 0 && <>
                    <div className="details-section-title">Timeline</div>
                    <div className="timeline-container" ref={timelineRef}>{renderTimeline()}</div>
                </>}

                <div className="details-section-title">Add Event</div>
                <div className="modal-field">
                    <label>Start</label>
                    <div className="modal-datetime">
                        <input type="date" value={startDate} onChange={e => handleStartDateChange(e.target.value)}/>
                        <input type="time" value={startTime} onChange={e => handleStartTimeChange(e.target.value)}/>
                    </div>
                </div>
                <div className="modal-field">
                    <label>End</label>
                    <div className="modal-datetime">
                        <input type="date" value={endDate} min={startDate} onChange={e => setEndDate(e.target.value)}/>
                        <input type="time" value={endTime} min={endTimeMin} onChange={e => setEndTime(e.target.value)}/>
                    </div>
                </div>
                {formError && <div className="modal-error">{formError}</div>}

                <div className="modal-buttons">
                    <button className="modal-btn" onClick={onClose}>Close</button>
                    <button className="modal-btn primary" onClick={handleSave} disabled={saving}>
                        {saving ? 'Saving…' : 'Add Event'}
                    </button>
                </div>
            </div>
        </div>
        </>
    );
}

export default DetailsModal;
